import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";
import { DemoBadge } from "@/components/shared";
import {
  BarChart3, Bell, Building2, ChevronsUpDown, CreditCard, FileText, Gauge,
  Globe, LayoutDashboard, Link2, ListChecks, LogOut, MapPin, Menu, PenTool, Plug, Rocket,
  Search, Settings, Target, User, Wrench,
} from "lucide-react";

const NAV_SECTIONS = [
  {
    label: "Insights",
    items: [
      { to: "/dashboard", end: true, icon: LayoutDashboard, label: "Overview", testId: "sidebar-nav-overview" },
      { to: "/dashboard/opportunities", icon: Target, label: "Opportunities", testId: "sidebar-nav-opportunities" },
      { to: "/dashboard/analytics", icon: BarChart3, label: "Analytics", testId: "sidebar-nav-analytics" },
    ],
  },
  {
    label: "Site",
    items: [
      { to: "/dashboard/pages", icon: FileText, label: "Pages", testId: "sidebar-nav-pages" },
      { to: "/dashboard/keywords", icon: Search, label: "Keywords", testId: "sidebar-nav-keywords" },
      { to: "/dashboard/locations", icon: MapPin, label: "Locations", testId: "sidebar-nav-locations" },
      { to: "/dashboard/content", icon: PenTool, label: "Content", testId: "sidebar-nav-content" },
      { to: "/dashboard/internal-links", icon: Link2, label: "Internal Links", testId: "sidebar-nav-internal-links" },
      { to: "/dashboard/technical", icon: Wrench, label: "Technical SEO", testId: "sidebar-nav-technical" },
    ],
  },
  {
    label: "Automation",
    items: [
      { to: "/dashboard/tasks", icon: ListChecks, label: "AI Tasks", testId: "sidebar-nav-tasks" },
      { to: "/dashboard/deployments", icon: Rocket, label: "Deployments", testId: "sidebar-nav-deployments" },
    ],
  },
  {
    label: "Admin",
    items: [
      { to: "/dashboard/connections", icon: Plug, label: "Connections", testId: "sidebar-nav-connections" },
      { to: "/dashboard/billing", icon: CreditCard, label: "Billing", testId: "sidebar-nav-billing" },
      { to: "/dashboard/settings", icon: Settings, label: "Settings", testId: "sidebar-nav-settings" },
    ],
  },
];

const SidebarNav = ({ onNavigate, activeWebsite }) => (
  <div className="flex h-full flex-col">
    <div className="flex h-14 items-center gap-2 border-b px-4">
      <div className="flex h-7 w-7 items-center justify-center rounded-md bg-primary text-primary-foreground">
        <Gauge className="h-4 w-4" />
      </div>
      <div className="leading-tight">
        <span className="block text-sm font-bold tracking-tight">SIX9 SEO</span>
        <span className="block text-[10px] font-medium text-muted-foreground">Control console</span>
      </div>
    </div>
    <nav className="flex-1 overflow-y-auto px-2 py-3">
      {NAV_SECTIONS.map((section) => (
        <div key={section.label} className="mb-4">
          <div className="px-2 pb-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground/70">{section.label}</div>
          {section.items.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              onClick={onNavigate}
              data-testid={item.testId}
              className={({ isActive }) =>
                `flex items-center gap-2.5 rounded-md px-2 py-1.5 text-sm font-medium ${
                  isActive ? "bg-muted/80 text-foreground border-l-2 border-primary -ml-0.5 pl-[7px]" : "text-muted-foreground hover:bg-muted/50 hover:text-foreground"
                }`
              }
            >
              <item.icon className="h-4 w-4" />
              {item.label}
            </NavLink>
          ))}
        </div>
      ))}
    </nav>
    <div className="border-t p-3">
      <NavLink
        to="/dashboard/connections"
        onClick={onNavigate}
        className="flex items-center gap-2 rounded-md border bg-muted/20 p-2.5 hover:bg-muted/50"
      >
        <span className={`h-2 w-2 rounded-full ${activeWebsite?.data_source === "google" ? "bg-emerald-500" : activeWebsite?.data_source === "demo" ? "bg-violet-500" : "bg-amber-500"}`} />
        <div className="min-w-0">
          <div className="text-xs font-medium">{activeWebsite?.domain || "No website selected"}</div>
          <div className="text-[10px] text-muted-foreground">{activeWebsite?.data_source === "google" ? "Live Google data" : activeWebsite?.data_source === "demo" ? "Using demo data" : "Connect a data source"}</div>
        </div>
      </NavLink>
    </div>
  </div>
);

export default function DashboardShell() {
  const { user, workspaces, websites, activeWorkspace, activeWebsite, setActiveWorkspace, setActiveWebsite, logout } = useApp();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    if (!activeWorkspace) return;
    let stop = false;
    const load = async () => {
      try {
        const { data } = await api.get(`/workspaces/${activeWorkspace.id}/notifications`);
        if (!stop) setNotifications(data);
      } catch (e) { /* noop */ }
    };
    load();
    const t = setInterval(load, 30000);
    return () => { stop = true; clearInterval(t); };
  }, [activeWorkspace]);

  const unread = notifications.filter((n) => !n.read).length;

  const markRead = async () => {
    if (!unread || !activeWorkspace) return;
    try {
      await api.post(`/workspaces/${activeWorkspace.id}/notifications/mark-read`);
      setNotifications((ns) => ns.map((n) => ({ ...n, read: true })));
    } catch (e) { /* noop */ }
  };

  return (
    <div className="flex min-h-screen bg-background">
      <aside className="hidden w-[240px] shrink-0 border-r bg-card lg:block">
        <SidebarNav activeWebsite={activeWebsite} />
      </aside>
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-20 flex h-14 items-center gap-2 border-b bg-card/95 px-3 backdrop-blur sm:px-4">
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="lg:hidden" data-testid="mobile-menu-button">
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-[260px] p-0">
              <SidebarNav activeWebsite={activeWebsite} onNavigate={() => setMobileOpen(false)} />
            </SheetContent>
          </Sheet>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="hidden h-8 max-w-[160px] gap-1.5 sm:inline-flex" data-testid="topbar-workspace-selector">
                <Building2 className="h-3.5 w-3.5 shrink-0" />
                <span className="truncate text-xs font-medium">{activeWorkspace?.name || "Workspace"}</span>
                <ChevronsUpDown className="h-3 w-3 shrink-0 text-muted-foreground" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-56">
              <DropdownMenuLabel className="text-xs">Workspaces</DropdownMenuLabel>
              {workspaces.map((ws) => (
                <DropdownMenuItem key={ws.id} onClick={() => setActiveWorkspace(ws)} data-testid={`workspace-option-${ws.id}`}>
                  {ws.name} {ws.id === activeWorkspace?.id && <span className="ml-auto text-xs text-primary">active</span>}
                </DropdownMenuItem>
              ))}
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => navigate("/onboarding?step=workspace")} data-testid="create-workspace-menu-item">+ New workspace</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="h-8 gap-1.5 max-w-[190px]" data-testid="topbar-website-selector">
                <Globe className="h-3.5 w-3.5 shrink-0" />
                <span className="truncate text-xs font-medium">{activeWebsite?.domain || "No website"}</span>
                <ChevronsUpDown className="h-3 w-3 shrink-0 text-muted-foreground" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-64">
              <DropdownMenuLabel className="text-xs">Websites</DropdownMenuLabel>
              {websites.map((s) => (
                <DropdownMenuItem key={s.id} onClick={() => setActiveWebsite(s)} data-testid={`website-option-${s.id}`}>
                  <span className="truncate">{s.domain}</span>
                  {s.id === activeWebsite?.id && <span className="ml-auto text-xs text-primary">active</span>}
                </DropdownMenuItem>
              ))}
              {websites.length === 0 && <DropdownMenuItem disabled>No websites yet</DropdownMenuItem>}
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => navigate("/onboarding?step=website")} data-testid="add-website-menu-item">+ Add website</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {activeWebsite?.data_source === "demo" && <DemoBadge />}
          {activeWebsite?.data_source === "google" && (
              <Badge variant="outline" className="hidden h-6 border-emerald-200 bg-emerald-50 px-2 text-xs font-medium text-emerald-700 md:inline-flex">
              Live data
              </Badge>
            )}

          <div className="ml-auto flex items-center gap-1">
            <Button variant="ghost" size="sm" className="hidden h-8 gap-1.5 text-xs text-muted-foreground sm:inline-flex" onClick={() => navigate("/dashboard/connections")}>
              <Plug className="h-3.5 w-3.5" /> Connections
            </Button>
            <DropdownMenu onOpenChange={(open) => open && markRead()}>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="relative h-8 w-8" data-testid="topbar-notifications-button">
                  <Bell className="h-4 w-4" />
                  {unread > 0 && (
                    <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-rose-600 px-1 text-[10px] font-bold text-white">
                      {unread}
                    </span>
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-80">
                <DropdownMenuLabel className="text-xs">Notifications</DropdownMenuLabel>
                {notifications.length === 0 && <div className="px-2 py-4 text-center text-xs text-muted-foreground">No notifications yet</div>}
                {notifications.slice(0, 8).map((n) => (
                  <div key={n.id} className="border-b px-2 py-2 last:border-0">
                    <div className="text-xs font-semibold">{n.title}</div>
                    <div className="text-xs text-muted-foreground">{n.message}</div>
                  </div>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-8 w-8" data-testid="topbar-profile-button">
                  <User className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52">
                <DropdownMenuLabel>
                  <div className="text-xs font-semibold">{user?.name}</div>
                  <div className="text-xs font-normal text-muted-foreground">{user?.email}</div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/dashboard/settings")} data-testid="profile-settings-menu-item">
                  <Settings className="mr-2 h-4 w-4" /> Settings
                </DropdownMenuItem>
                <DropdownMenuItem onClick={async () => { await logout(); navigate("/login"); }} data-testid="logout-menu-item">
                  <LogOut className="mr-2 h-4 w-4" /> Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>
        <main className="mx-auto w-full max-w-[1600px] flex-1 px-3 py-4 sm:px-6 sm:py-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
